// Translates external video URLs into embeddable iframe sources.
// Returns null when the URL cannot be embedded in-page; callers should fall
// back to opening the original URL in a new tab.

export interface EmbedResult {
  src: string;
  /** Aspect ratio hint (width / height). Defaults to 16/9 when unknown. */
  aspectRatio?: number;
  /** Allow list for the iframe's `allow` attribute. */
  allow?: string;
}

export function toEmbedUrl(rawUrl: string): EmbedResult | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  const host = url.hostname.toLowerCase();

  // ─── YouTube ──────────────────────────────────────────────────────────────
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    if (!id) return null;
    return {
      src: `https://www.youtube-nocookie.com/embed/${id}`,
      allow:
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
    };
  }
  if (host.endsWith('youtube.com')) {
    const id =
      url.searchParams.get('v') ||
      (url.pathname.startsWith('/embed/') ? url.pathname.split('/')[2] : null) ||
      (url.pathname.startsWith('/shorts/') ? url.pathname.split('/')[2] : null);
    if (!id) return null;
    return {
      src: `https://www.youtube-nocookie.com/embed/${id}`,
      allow:
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
    };
  }

  // ─── Vimeo ────────────────────────────────────────────────────────────────
  if (host.endsWith('vimeo.com')) {
    // /VIDEOID or /channels/.../VIDEOID
    const segments = url.pathname.split('/').filter(Boolean);
    const id = segments[segments.length - 1];
    if (!id || !/^\d+$/.test(id)) return null;
    return {
      src: `https://player.vimeo.com/video/${id}`,
      allow: 'autoplay; fullscreen; picture-in-picture',
    };
  }

  // ─── Zoom cloud recordings ────────────────────────────────────────────────
  // Public Zoom recording URLs look like:
  //   https://us02web.zoom.us/rec/share/<token>
  //   https://*.zoom.us/rec/play/<token>
  // The `share/` form redirects to a play page; the `play/` form serves the
  // actual player. Both render inside an iframe most of the time, but Zoom
  // sometimes blocks X-Frame-Options on `share/`. We attempt embedding and
  // rely on the iframe `onError` UI to suggest the new-tab fallback.
  if (host.endsWith('zoom.us') && url.pathname.startsWith('/rec/')) {
    return { src: rawUrl, allow: 'autoplay; encrypted-media; fullscreen' };
  }

  // ─── Generic fallback ─────────────────────────────────────────────────────
  // Allow direct .mp4/.webm/.mov URLs via <video> wrapper (handled by caller).
  return null;
}

export function isPlayableVideoFile(url: string): boolean {
  return /\.(mp4|webm|mov|ogv)(\?|$)/i.test(url);
}
